import { zodResolver } from '@hookform/resolvers/zod';
import Link from 'next/link';
import { FC, useRef } from 'react';
import { Controller, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import { sendForm } from '@/app/actions/send-form';
import BrandBookFormCloseButton from '@/components/BrandBookFormCloseButton';
import BrandBookFormContainer from '@/components/BrandBookFormContainer';
import Input from '@/components/shared/Fields/input';
import CustomDropdown from '@/components/shared/Fields/select';
import Textarea from '@/components/shared/Fields/textarea';
import Honeypot, { HoneypotHandle } from '@/components/shared/Honeypot';
import { BrandbookFormData, brandbookFormSchema } from '@/constants/form';
import { howYouFindQreamOptions, HowYouFindQreamValue } from '@/constants/how-you-find';
import routes from '@/constants/routes';
import { toastErrorStyles } from '@/constants/toast';
import css from './style.module.scss';
interface Props {
  onClose: () => void;
  onSend: () => void;
}
const BrandBookFormActive: FC<Props> = ({
  onClose,
  onSend
}) => {
  const {
    control,
    handleSubmit,
    formState: {
      errors,
      isSubmitting
    }
  } = useForm<BrandbookFormData>({
    resolver: zodResolver(brandbookFormSchema),
    mode: 'onChange'
  });
  const howYouFindQreamValue = useWatch({
    control,
    name: 'howYouFindQream'
  });
  const honeypotRef = useRef<HoneypotHandle>(null);
  const onSubmit: SubmitHandler<BrandbookFormData> = async (data, event) => {
    event?.preventDefault();
    try {
      await sendForm('/content-forms', {
        ...data,
        ...honeypotRef.current?.getGuardFields()
      } as any);
      onSend();
    } catch {
      toast.error('Error submitting form', toastErrorStyles);
    }
  };
  return <BrandBookFormContainer>
			<BrandBookFormCloseButton onClose={onClose} />

			<p className={css.title}>Let’s make it official</p>

			<p className={css.text}>Drop your details, and we’ll shoot over your Q-brandbook.</p>

			<form onSubmit={handleSubmit(onSubmit)}>
				<Honeypot ref={honeypotRef} />

				<div className={css.fieldsWrap}>
					<div className={css.field}>
						<Controller name="name" control={control} defaultValue="" render={({
            field
          }) => <Input allowedPattern={/^[A-Za-zА-Яа-яЁёІіЇїЄєҐґ\s'-]*$/} {...field} placeholder="Your name" />} />

						{errors.name?.message ? <p className={css.error}>{errors.name.message}</p> : null}
					</div>

					<div className={css.field}>
						<Controller name="email" control={control} defaultValue="" render={({
            field: field_0
          }) => <Input {...field_0} placeholder="Your email" />} />

						{errors.email?.message ? <p className={css.error}>{errors.email.message}</p> : null}
					</div>

					<div className={css.field}>
						<Controller name="jobTitle" control={control} defaultValue="" render={({
            field: field_1
          }) => <Input {...field_1} placeholder="Job title" />} />
					</div>

					<div className={css.field}>
						<Controller name="howYouFindQream" control={control} defaultValue="" render={({
            field: field_2
          }) => <CustomDropdown {...field_2} options={howYouFindQreamOptions} value={howYouFindQreamOptions.find(option => option.value === field_2.value) || null} />} />
					</div>

					{howYouFindQreamValue === HowYouFindQreamValue.Other && <div className={css.field}>
							<Controller name="howYouFindQreamOther" control={control} defaultValue="" render={({
            field: field_3
          }) => <Input {...field_3} placeholder="Let us know where you heard about us" />} />
							{errors.howYouFindQreamOther?.message ? <p className={css.error}>{errors.howYouFindQreamOther.message}</p> : null}
						</div>}

					<div className={css.field}>
						<Controller name="details" control={control} defaultValue="" render={({
            field: field_4
          }) => <Textarea {...field_4} placeholder="Describe your project" />} />
					</div>
				</div>

				<div className={css.buttonContainer}>
					<button disabled={isSubmitting} className={css.submitButton} type="submit">
						Get brandbook
					</button>
				</div>
			</form>

			<div className={css.footer}>
				<p className={css.footerText}>
					Curious how we handle your data with care? Scoop into our{' '}
					<Link className={css.link} href={routes.privacyPolicy}>
						Privacy Policy
					</Link>
				</p>

				<span className={css.footerYear}>{new Date().getFullYear()}</span>
			</div>
		</BrandBookFormContainer>;
};
export default BrandBookFormActive;